import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellRing, ChefHat, Printer } from "lucide-react";
import { api } from "@/pos/lib/api";
import { printOnline } from "@/pos/lib/print";
import { playNewOrderChime, unlockAudio } from "@/lib/chime";
import { TYPE_LABEL } from "@/pos/lib/restaurant";

const money = (n) => `RM${Number(n || 0).toFixed(2)}`;

function useNewOnlineOrders() {
  const [queue, setQueue] = useState([]);
  const seen = useRef(null);
  useEffect(() => {
    window.addEventListener("pointerdown", unlockAudio, { once: true });
    const tick = async () => {
      try {
        const { data } = await api.get("/kitchen");
        const online = [...data.pending, ...data.preparing, ...data.ready].filter((o) => o.source === "online");
        if (seen.current === null) { seen.current = new Set(online.map((o) => o.id)); return; }
        const fresh = online.filter((o) => !seen.current.has(o.id));
        fresh.forEach((o) => seen.current.add(o.id));
        if (fresh.length) setQueue((q) => [...q, ...fresh]);
      } catch (e) { /* cuba lagi */ }
    };
    tick();
    const t = setInterval(tick, 8000);
    return () => { clearInterval(t); window.removeEventListener("pointerdown", unlockAudio); };
  }, []);
  return [queue, () => setQueue((q) => q.slice(1))];
}

export default function NewOrderAlert() {
  const [queue, dismiss] = useNewOnlineOrders();
  const nav = useNavigate();
  const o = queue[0];
  useEffect(() => {
    if (!o) return undefined;
    playNewOrderChime();
    const t = setInterval(playNewOrderChime, 2500);
    return () => clearInterval(t);
  }, [o]);
  if (!o) return null;
  const reprint = async () => { await api.post(`/orders/${o.id}/print-claim`).catch(() => {}); printOnline(o); };
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/60 p-4 backdrop-blur-sm" data-testid="new-order-alert">
      <div className="w-full max-w-md animate-in zoom-in-95 rounded-3xl bg-white p-6 shadow-2xl">
        <div className="flex items-center gap-3">
          <div className="grid h-14 w-14 animate-pulse place-items-center rounded-2xl bg-amber-400 text-white"><BellRing size={30} /></div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-amber-600">Pesanan App Baharu</div>
            <div className="font-heading text-3xl font-extrabold" data-testid="new-order-alert-no">{o.order_no || `#${o.number}`}</div>
          </div>
          {queue.length > 1 && <span className="ml-auto rounded-full bg-red-500 px-2.5 py-1 text-xs font-bold text-white" data-testid="new-order-alert-more">+{queue.length - 1}</span>}
        </div>
        <div className="mt-4 rounded-2xl bg-slate-50 p-4 text-sm">
          <div className="flex justify-between font-semibold"><span data-testid="new-order-alert-customer">{o.customer_name}</span><span>{TYPE_LABEL[o.type] || o.type}</span></div>
          <div className="text-slate-500">{o.customer_phone}</div>
          <ul className="mt-3 max-h-40 space-y-1 overflow-y-auto">
            {o.items.map((it, i) => <li key={i} className="flex justify-between"><span>{it.qty} x {it.name}</span><span>{money(it.subtotal)}</span></li>)}
          </ul>
          <div className="mt-3 flex justify-between border-t pt-2 text-base font-bold"><span>Jumlah</span><span data-testid="new-order-alert-total">{money(o.total)}</span></div>
          <div className={`mt-2 text-center text-xs font-bold ${o.online_paid ? "text-green-600" : "text-amber-600"}`}>{o.online_paid ? "SUDAH DIBAYAR ONLINE" : "BAYAR DI KAUNTER"}</div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-2">
          <button onClick={reprint} className="btn-outline justify-center" data-testid="new-order-alert-print"><Printer size={16} /> Cetak</button>
          <button onClick={() => { dismiss(); nav("/pos/dapur"); }} className="btn-outline justify-center" data-testid="new-order-alert-kitchen"><ChefHat size={16} /> Ke Dapur</button>
          <button onClick={dismiss} className="btn-primary col-span-2 justify-center py-3 text-base" data-testid="new-order-alert-ack">Terima</button>
        </div>
      </div>
    </div>
  );
}
